import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { tx } from '../i18n';
import { motivosDeAtencion } from '../lib/appliance';
import { duracion } from '../lib/formato';
import type { ApplianceFila } from '../tipos';

/**
 * El parque en un radar (el mismo de la app del cliente, con otro significado).
 *
 * Cada eco es un appliance. **La distancia al centro es hace cuánto que no se lo ve**: en el
 * centro los que acaban de latir, y cada anillo es diez veces más tiempo (1 min, 10 min, 1 h,
 * 10 h). Uno que se aleja está perdiendo contacto, y se ve venir antes de que alguien lea una
 * lista. Afuera del último anillo va lo que nunca conectó.
 *
 * El color es su estado: verde el que está bien, ámbar el que tiene algo (disco, motores),
 * rojo el que está sin contacto. El sector es su organización, así lo de un cliente queda
 * junto.
 */

const MAX_SECTORES = 12;
/** Los anillos, en minutos desde el último latido, con su rótulo. El último es el borde. */
const ANILLOS: { min: number; rotulo: string }[] = [
  { min: 1, rotulo: '1 min' },
  { min: 10, rotulo: '10 min' },
  { min: 60, rotulo: '1 h' },
  { min: 600, rotulo: '10 h' },
];
const SIN_CONTACTO_MIN = 10;

interface Eco {
  a: ApplianceFila;
  x: number;
  y: number;
  tono: 'ok' | 'aviso' | 'mal' | 'nd';
  minutos: number | null;
}

interface Sector {
  nombre: string;
  desde: number;
  hasta: number;
}

/** Qué tan lejos del centro: escala logarítmica, como la latencia en el radar del cliente. */
function distancia(minutos: number | null): number {
  if (minutos === null) return 1.08; // nunca conectó: afuera del último anillo
  const max = Math.log10(1 + ANILLOS.at(-1)!.min);
  return Math.min(1.08, Math.max(0.04, Math.log10(1 + Math.max(0, minutos)) / max));
}

function tonoDe(a: ApplianceFila): Eco['tono'] {
  if (a.estado !== 'activo') return 'nd';
  if (!a.en_linea) return 'mal';
  return motivosDeAtencion(a).length > 0 ? 'aviso' : 'ok';
}

/** Un ángulo estable por appliance: el radar no baila entre refrescos. */
function semilla(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return (h % 1000) / 1000;
}

/**
 * Un color con transparencia, para el lienzo. Ojo: el canvas **no** entiende `color-mix()`
 * —`addColorStop` tira excepción y se queda todo en blanco—, así que se arma a mano desde
 * lo que dicen las variables del tema.
 */
function conAlfa(color: string, a: number): string {
  const c = color.trim();
  const hex = /^#([0-9a-f]{6})$/i.exec(c);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
  }
  const rgb = /^rgba?\(([^)]+)\)$/i.exec(c);
  if (rgb) {
    const [r, g, b] = rgb[1].split(',').map((x) => parseFloat(x));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return c;
}

function paleta() {
  const cs = getComputedStyle(document.documentElement);
  const v = (n: string) => cs.getPropertyValue(n).trim();
  return {
    ok: v('--ok'),
    aviso: v('--parcial'),
    mal: v('--caido'),
    nd: v('--nd'),
    linea: v('--linea'),
    linea2: v('--linea2'),
    tenue: v('--tenue'),
    dim: v('--dim'),
    panel: v('--panel'),
    acento: v('--acento'),
  };
}

export function RadarDelParque({ appliances }: { appliances: ApplianceFila[] }) {
  const lienzo = useRef<HTMLCanvasElement>(null);
  const caja = useRef<HTMLDivElement>(null);
  const navegar = useNavigate();
  const [encima, setEncima] = useState<Eco | null>(null);
  const ecos = useRef<Eco[]>([]);

  useEffect(() => {
    const cv = lienzo.current;
    const cont = caja.current;
    if (!cv || !cont) return;
    let vivo = true;
    let inicio = performance.now();

    const pintar = () => {
      if (!vivo) return;
      const dpr = window.devicePixelRatio || 1;
      const W = cont.clientWidth;
      const H = cont.clientHeight;
      if (cv.width !== W * dpr || cv.height !== H * dpr) {
        cv.width = W * dpr;
        cv.height = H * dpr;
        cv.style.width = `${W}px`;
        cv.style.height = `${H}px`;
      }
      const g = cv.getContext('2d');
      if (!g) return;
      const c = paleta();
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, W, H);

      const cx = W / 2;
      const cy = H / 2;
      const R = Math.max(40, Math.min(W, H) / 2 - 26);

      // los sectores: un cliente por tajada, lo más grande primero
      const porOrg = new Map<string, ApplianceFila[]>();
      for (const a of appliances) {
        if (a.estado === 'revocado') continue;
        const lista = porOrg.get(a.organizacion) ?? [];
        lista.push(a);
        porOrg.set(a.organizacion, lista);
      }
      const orgs = [...porOrg.entries()].sort((x, y) => y[1].length - x[1].length).slice(0, MAX_SECTORES);
      const total = orgs.reduce((s, [, l]) => s + l.length, 0) || 1;
      const sectores: Sector[] = [];
      let ang = -Math.PI / 2;
      for (const [nombre, lista] of orgs) {
        const paso = (lista.length / total) * Math.PI * 2;
        sectores.push({ nombre, desde: ang, hasta: ang + paso });
        ang += paso;
      }

      // el fondo: los anillos de tiempo
      g.lineWidth = 1;
      for (const anillo of ANILLOS) {
        const r = distancia(anillo.min) * R;
        g.strokeStyle = anillo === ANILLOS.at(-1) ? c.linea2 : c.linea;
        g.beginPath();
        g.arc(cx, cy, r, 0, Math.PI * 2);
        g.stroke();
        g.fillStyle = c.tenue;
        g.font = '10px system-ui, sans-serif';
        g.textAlign = 'center';
        g.fillText(anillo.rotulo, cx, cy - r - 4);
      }
      // las líneas entre sectores, que dicen dónde empieza cada cliente
      if (sectores.length > 1) {
        g.strokeStyle = c.linea;
        for (const s of sectores) {
          g.beginPath();
          g.moveTo(cx, cy);
          g.lineTo(cx + Math.cos(s.desde) * R * 1.1, cy + Math.sin(s.desde) * R * 1.1);
          g.stroke();
        }
      }

      // el barrido: da la vuelta cada 8 s, como el de la marca
      const t = ((performance.now() - inicio) / 8000) % 1;
      const barrido = t * Math.PI * 2 - Math.PI / 2;
      const cono = g.createConicGradient?.(barrido - 0.9, cx, cy);
      if (cono) {
        cono.addColorStop(0, conAlfa(c.acento, 0));
        cono.addColorStop(0.24, conAlfa(c.acento, 0.12));
        cono.addColorStop(0.25, conAlfa(c.acento, 0.26));
        cono.addColorStop(0.2501, conAlfa(c.acento, 0));
        g.fillStyle = cono;
        g.beginPath();
        g.arc(cx, cy, R * 1.1, 0, Math.PI * 2);
        g.fill();
      }

      // los ecos: uno por appliance, en su sector
      const nuevos: Eco[] = [];
      sectores.forEach((s, si) => {
        const lista = porOrg.get(s.nombre)!;
        lista.forEach((a, i) => {
          const hueco = (s.hasta - s.desde) / Math.max(1, lista.length);
          const jitter = (semilla(a.id) - 0.5) * hueco * 0.5;
          const ai = s.desde + hueco * (i + 0.5) + jitter;
          const minutos = a.visto ? (Date.now() - Date.parse(a.visto)) / 60_000 : null;
          const d = distancia(minutos) * R;
          const eco: Eco = { a, x: cx + Math.cos(ai) * d, y: cy + Math.sin(ai) * d, tono: tonoDe(a), minutos };
          nuevos.push(eco);
          const color = c[eco.tono];
          // el eco brilla cuando el barrido le pasa por encima
          const delta = Math.abs(((ai - barrido + Math.PI * 4) % (Math.PI * 2)) - 0) % (Math.PI * 2);
          const brillo = Math.max(0, 1 - delta / 0.9);
          const r = 4 + (eco.tono === 'mal' ? 1.5 : 0) + brillo * 3;
          if (brillo > 0.02) {
            g.fillStyle = conAlfa(color, brillo * 0.45);
            g.beginPath();
            g.arc(eco.x, eco.y, r + 7, 0, Math.PI * 2);
            g.fill();
          }
          g.fillStyle = color;
          g.beginPath();
          g.arc(eco.x, eco.y, r, 0, Math.PI * 2);
          g.fill();
          if (si >= 0 && lista.length && i === 0 && sectores.length > 1) {
            // el nombre del cliente, sobre el borde de su sector
            const am = (s.desde + s.hasta) / 2;
            g.fillStyle = c.dim;
            g.font = '600 10px system-ui, sans-serif';
            g.textAlign = Math.cos(am) < -0.2 ? 'right' : Math.cos(am) > 0.2 ? 'left' : 'center';
            g.fillText(s.nombre, cx + Math.cos(am) * (R * 1.14), cy + Math.sin(am) * (R * 1.14) + 3);
          }
        });
      });
      ecos.current = nuevos;

      requestAnimationFrame(pintar);
    };
    const id = requestAnimationFrame(pintar);
    return () => {
      vivo = false;
      cancelAnimationFrame(id);
      inicio = 0;
    };
  }, [appliances]);

  return (
    <>
      <p className="radar-pie">{tx.tablero.radarQue}</p>
      <div className="radar" ref={caja}>
        <canvas
          ref={lienzo}
          onMouseMove={(e) => {
            const r = lienzo.current!.getBoundingClientRect();
            const x = e.clientX - r.left;
            const y = e.clientY - r.top;
            const cerca = ecos.current.find((p) => Math.hypot(p.x - x, p.y - y) < 12);
            setEncima(cerca ?? null);
          }}
          onMouseLeave={() => setEncima(null)}
          onClick={() => encima && navegar(`/appliances/${encima.a.id}`)}
          style={{ cursor: encima ? 'pointer' : 'default' }}
        />
        {encima && (
          <div className="radar-globo" style={{ left: encima.x, top: encima.y }}>
            <b>{encima.a.nombre}</b>
            <small>{encima.a.organizacion}</small>
            <span>
              {encima.minutos === null
                ? tx.estado.nuncaConecto
                : encima.minutos >= SIN_CONTACTO_MIN
                  ? tx.estado.sinContacto(duracion(encima.minutos * 60_000))
                  : tx.comun.hace(duracion(encima.minutos * 60_000))}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
